import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { UpdateOrganizationAppBrandingRequest } from "@/types/organizations";

/**
 * Per-organization employee-app branding: the optional accent colour and the
 * "Brand the employee app" switch. Backed by the CMS API
 * `PUT /organizations/{organizationId}/app-branding` (read back on
 * `GET /organizations/{id}`). The main `PUT /organizations/{id}` never touches
 * these two fields, so the edit dialog saves them with this second call.
 *
 * A blank accent is sent as `null`, which CLEARS it — the app then uses a
 * darker shade of the secondary (highlight) colour.
 *
 * Success toast is intentionally left to the caller: the edit dialog shows ONE
 * toast after both saves succeed. Failures toast here.
 */
export function useUpdateOrgAppBranding(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    // Never retry: a failed save must surface once (the app default retries mutations).
    retry: false,
    mutationFn: async (data: UpdateOrganizationAppBrandingRequest) => {
      if (!organizationId) throw new Error("No organization selected");
      const accent = data.accentColor?.trim();
      const payload: UpdateOrganizationAppBrandingRequest = {
        accentColor: accent ? accent : null,
        employeeAppBrandingEnabled: data.employeeAppBrandingEnabled,
      };
      return apiRequest("PUT", `/organizations/${organizationId}/app-branding`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/organizations"] });
      queryClient.invalidateQueries({ queryKey: [`/organizations/${organizationId}`] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to save the employee app branding. Please try again.",
        variant: "destructive",
      });
    },
  });
}
